import React from "react";
import PolicyLayout, { PolicyH2, PolicyP, PolicyUL, PolicyCallout } from "../components/app/PolicyLayout";

const TOC = [
  { id: "what", label: "1. What cookies are" },
  { id: "which", label: "2. Which we use" },
  { id: "consent", label: "3. Your consent" },
  { id: "change", label: "4. Change your choice" },
  { id: "third", label: "5. Third-party cookies" },
  { id: "contact", label: "6. Contact" },
];

export default function Cookies() {
  return (
    <PolicyLayout
      title="Cookie Policy"
      eyebrow="Legal · ICO-compliant · Plain English"
      lastUpdated="23 April 2026"
      toc={TOC}
      testid="cookies-page"
    >
      <PolicyP>
        Cookies are small files your browser keeps so our app can remember you and work properly. We try to use as
        few as possible — and none of them are advertising cookies.
      </PolicyP>

      <PolicyH2 id="what">1. What cookies are</PolicyH2>
      <PolicyP>
        Cookies are tiny text files websites save on your device. Some are essential (e.g. keeping you signed in),
        others are optional (e.g. helping us understand which features campers use most).
      </PolicyP>

      <PolicyH2 id="which">2. Which cookies we use</PolicyH2>

      <h3 className="font-display text-lg mb-3 mt-8">Essential — always on</h3>
      <PolicyUL>
        <li><code className="mono text-xs bg-black/5 px-1">cc_token</code> — keeps you signed in (JWT, 7 days).</li>
        <li><code className="mono text-xs bg-black/5 px-1">cc-cookie-consent-v1</code> — remembers your cookie choice (localStorage).</li>
        <li><code className="mono text-xs bg-black/5 px-1">cc-compare-ids</code> — your current campsite comparison (localStorage).</li>
      </PolicyUL>

      <h3 className="font-display text-lg mb-3 mt-8">Analytics — optional (needs your consent)</h3>
      <PolicyUL>
        <li><strong>PostHog</strong> — anonymous usage stats (page views, clicks on "Book on Pitchup", Pro trial starts). No personal info. No session replay by default. Stored in PostHog EU servers.</li>
      </PolicyUL>

      <h3 className="font-display text-lg mb-3 mt-8">Preferences — optional</h3>
      <PolicyUL>
        <li><code className="mono text-xs bg-black/5 px-1">cc-announcement-dismissed</code> — hides the "NEW campsites" banner you've closed.</li>
      </PolicyUL>

      <PolicyH2 id="consent">3. Your consent</PolicyH2>
      <PolicyP>
        On your first visit, our cookie banner lets you <strong>Accept all</strong>, <strong>Reject non-essential</strong>, or{" "}
        <strong>Customise</strong>. We don't load analytics cookies until you say yes. Essential cookies are always on because
        the app can't work without them (UK GDPR and PECR both allow this).
      </PolicyP>

      <PolicyH2 id="change">4. Change your choice</PolicyH2>
      <PolicyCallout>
        You can change your mind any time by clearing site data in your browser settings, or by emailing{" "}
        <a href="mailto:dpo@camp-compass.uk" className="link-underline text-[#ff4500]">dpo@camp-compass.uk</a>.
      </PolicyCallout>

      <PolicyH2 id="third">5. Third-party cookies</PolicyH2>
      <PolicyP>
        When you leave the app to book on Pitchup, Pitchup sets its own cookies — we have no control over them. Check{" "}
        <a href="https://www.pitchup.com/privacy/" target="_blank" rel="noopener noreferrer" className="link-underline text-[#ff4500]">Pitchup's privacy policy</a> for details.
      </PolicyP>
      <PolicyP>
        Stripe sets essential payment cookies during the checkout flow to prevent fraud — see{" "}
        <a href="https://stripe.com/cookies-policy/legal" target="_blank" rel="noopener noreferrer" className="link-underline text-[#ff4500]">Stripe's cookie policy</a>.
      </PolicyP>

      <PolicyH2 id="contact">6. Contact</PolicyH2>
      <PolicyP>
        Questions? Email us at <a href="mailto:hello@camp-compass.uk" className="link-underline text-[#ff4500]">hello@camp-compass.uk</a> — we read everything.
      </PolicyP>
    </PolicyLayout>
  );
}
